/**
 * 登录页（旧 views/auth/LoginView.vue）：登录成功后回到 ?redirect= 指定的页面
 */
import { App, Button, Checkbox, Form, Input } from 'antd'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth'
import { safeRedirect } from '@/router/guards'
import type { LoginCredentials } from '@/types/admin'
import styles from './LoginPage.module.css'

const loginRouteApi = getRouteApi('/login')

export function LoginPage() {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const { redirect } = loginRouteApi.useSearch()
  const isLoading = useAuthStore((state) => state.isLoading)

  const handleLogin = async (values: LoginCredentials) => {
    const success = await useAuthStore.getState().login(values)
    if (success) {
      await navigate({ href: safeRedirect(redirect) })
    } else {
      message.error('登录失败，请检查用户名和密码')
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logo}>🎯</div>
          <h2 className={styles.title}>AcquiLang AI</h2>
          <p className={styles.subtitle}>管理中台系统</p>
        </div>

        <Form<LoginCredentials>
          layout="vertical"
          autoComplete="off"
          initialValues={{ username: '', password: '', remember: false }}
          onFinish={handleLogin}
        >
          <Form.Item
            name="username"
            label="用户名"
            hasFeedback
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名长度不能少于3位' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入管理员用户名"
              size="large"
              autoComplete="username"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            hasFeedback
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度不能少于6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入管理员密码"
              size="large"
              autoComplete="current-password"
              disabled={isLoading}
            />
          </Form.Item>

          <div className={styles.options}>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox disabled={isLoading}>记住登录状态（7天）</Checkbox>
            </Form.Item>
            <Button type="link" className={styles.forgot} onClick={() => message.info('请联系系统管理员重置密码')}>
              忘记密码？
            </Button>
          </div>

          <Button type="primary" htmlType="submit" size="large" block loading={isLoading} className={styles.submit}>
            {isLoading ? '正在登录...' : '登录管理中台'}
          </Button>
        </Form>

        <div className={styles.footer}>
          <p>&copy; 2025 AcquiLang AI. All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}
